"use client";

import React from "react";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Cpu, Gauge } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const solutions = [{
  id: 1,
  title: "Process NIR Spectrometers",
  tagline: "Inline & At-Line Analysis",
  description: "High-precision near-infrared spectroscopy systems engineered for continuous monitoring of reaction dynamics, blend uniformity, and raw material verification.",
  iconName: "activity",
  featureOne: "Rapid non-destructive spectral sampling",
  featureTwo: "Robust industrial enclosure designs",
  featureThree: "Seamless laboratory-to-process transfer",
  linkPath: "/ProductsPage"
}, {
  id: 2,
  title: "In-Situ Moisture & Multicomponent Probes",
  tagline: "Direct Process Immersion",
  description: "Rugged optical immersion and contact probes tailored for harsh environments, powder drying, slurry streams, and precise multi-component quantification.",
  iconName: "gauge",
  featureOne: "High-temperature and pressure resilience",
  featureTwo: "Customizable optical path lengths",
  featureThree: "Sanitary flanges for regulated environments",
  linkPath: "/ProductsPage"
}, {
  id: 3,
  title: "Real-Time PAT Software & Chemometric Engines",
  tagline: "Process Analytical Intelligence",
  description: "Integrated software architectures combining real-time spectral acquisition, multivariate chemometric modeling, and automated data compliance reporting.",
  iconName: "cpu",
  featureOne: "Automated qualitative & quantitative models",
  featureTwo: "Direct SCADA / DCS integration support",
  featureThree: "Compliant audit trails & data governance",
  linkPath: "/SolutionsPage"
}];
export default function Solutions() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="productsolutions" data-nav="light" className="relative w-full bg-background py-20 md:py-28 text-foreground">
      <div className="page-container">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary bg-primary/10 px-3 py-1 font-medium tracking-wide uppercase text-xs">
            Analytical Systems &amp; PAT
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Engineered Solutions for Regulated Manufacturing
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Application-focused hardware, optical interfaces, and chemometric platforms configured to support compliant pharmaceutical, chemical, and biotechnology workflows.
          </p>
        </div>

        {/* 3-Column Solutions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {solutions.map((item, index) => {
          return <motion.div key={item.id} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.5,
            delay: shouldReduceMotion ? 0 : index * 0.15,
            ease: "easeOut"
          }} className="flex flex-col h-full">
                <Card className="flex flex-col justify-between h-full bg-card border border-border shadow-md hover:shadow-lg transition-[box-shadow,border-color] duration-300 hover:border-primary/40 rounded-xl overflow-hidden">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="size-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center border border-primary/20">
                        {item.iconName === "activity" && <Activity className="size-6 text-primary" />}
                        {item.iconName === "gauge" && <Gauge className="size-6 text-primary" />}
                        {item.iconName === "cpu" && <Cpu className="size-6 text-primary" />}
                      </div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                        0{index + 1}
                      </span>
                    </div>
                    <div className="text-xs font-medium text-primary tracking-wide mb-1">
                      {item.tagline}
                    </div>
                    <CardTitle className="font-default text-xl font-bold text-card-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2 pb-6 flex-grow">
                    <div className="border-t border-border/80 pt-4 mt-2 space-y-3">
                      <div className="flex items-start gap-2.5 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{item.featureOne}</span>
                      </div>
                      <div className="flex items-start gap-2.5 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{item.featureTwo}</span>
                      </div>
                      <div className="flex items-start gap-2.5 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{item.featureThree}</span>
                      </div>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-0 pb-6 border-t border-border/40 mt-auto">
                    <Button variant="outline" asChild className="w-full justify-between group hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">
                      <Link to={item.linkPath}>
                        <span>Explore Technology</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Bottom CTA Strip */}
        <div className="mt-16 text-center border-t border-border pt-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            Need custom optical configurations or tailored method validation for your process?
          </p>
          <Button asChild variant="default" size="default" className="font-medium">
            <Link to="/ContactPage">
              <span>Consult an Application Specialist</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}